
import Pay from "./com/pay.vue";
import Tester from "./com/tester/index.vue";
import RegisterMini from "./com/register-mini/index.vue";
import { Action, Getter, Mutation, namespace, State } from 'vuex-facing-decorator';
import { Component, Prop, Vue, Watch } from 'vue-facing-decorator'

@Component({})
export default class extends Vue {
  @State((state) => state.store.store)
  store;

  get query() {
    return this.$route.query;
  }
  get authString() {
    if (!this.query) return false;
    return this.query.authString;
  }

  get authPost() {
    return {
      authString: this.query.authString,
      time: this.query.time,
      authCode: this.query.auth_code,
      expiresIn: this.query.expires_in,
    }
  }

  registerStatus: any = null;
  authLoading = false;
  /** { remote: 微信最新审核单 | null, local: { auditing, online, history } } */
  auditInfo: any = null;
  auditLoading = false;
  pollTimer: any = null;

  /**
   * 首次加载放这里而不是 created：布局的 getStore() 和页面 created 谁先谁后不确定，
   * 在 created 里读 store.appid 很可能还是空的，读了就再也不会补
   */
  @Watch('store.appid', { immediate: true })
  appidWatch(appid: any) {
    if (!appid) return;
    this.loadAuditResult();
  }

  get remote() {
    return this.auditInfo ? this.auditInfo.remote : null;
  }
  get local() {
    return this.auditInfo ? this.auditInfo.local : null;
  }
  get auditing() {
    return (this.local && this.local.auditing) || [];
  }
  /** 本地那条「审核中」的记录 */
  get auditRow() {
    return this.auditing.find((item: any) => item.status == 1) || null;
  }
  /** 已过审、还没发版的那条 */
  get passRow() {
    return this.auditing.find((item: any) => item.status == 3) || null;
  }
  get failRow() {
    return this.auditing.find((item: any) => item.status == 2) || null;
  }

  /**
   * 还没拿到数据时一律不禁用：接口挂了不能让商家彻底点不动
   * 注意别拿 auditing.length 当「审核中」用，那个数组里还有失败和待发版
   */
  get canSubmit() {
    if (!this.auditInfo) return true;
    return this.remote?.status != 2 && !this.auditRow;
  }
  /** 撤回的前置条件就是微信侧「审核中」 */
  get canWithdraw() {
    if (!this.auditInfo) return true;
    return this.remote?.status == 2;
  }
  get canRelease() {
    if (!this.auditInfo) return true;
    return !!this.passRow;
  }
  /**
   * 审核中/延后要继续问；微信给了终态就停 ——
   * 本地可能留着对不上的旧记录（比如丢推送那次已经发过版了），
   * 拿它当依据会一直轮询下去
   */
  get needPoll() {
    let status = this.remote ? this.remote.status : null;
    if (status == 2 || status == 4) return true;
    /** remote 没拿到（接口失败或从没提审过）时，本地还有审核中的记录就接着问 */
    return !this.remote && !!this.auditRow;
  }

  /**
   * 状态条文案。终态优先判断，别让中间状态盖掉结果
   * （同 src/views/shop/wechat/index/page/auth.vue 的 auditStatus）
   */
  get auditStatus() {
    let remote = this.remote;
    let version = remote && remote.version ? `${remote.version} · ` : '';
    let submitTime = remote && remote.submitTime ? this.$format(remote.submitTime * 1000) : '';
    if (!remote) {
      /** 86001：从来没提审过 */
      if (this.local && this.local.online) {
        return this.onlineStatus();
      }
      return {
        type: 'none',
        title: '还未提交审核',
        desc: '准备好后点「提交审核」，微信审核最长 14 个工作日',
      };
    }
    switch (remote.status) {
      case 2:
        return {
          type: 'pending',
          title: '审核中',
          desc: `${version}${submitTime} 提交，微信审核最长 14 个工作日`,
        };
      case 4:
        return {
          type: 'pending',
          title: '审核延后',
          desc: '这次审核微信排在后面了，版本还在队列里，可以再等等',
        };
      case 3:
        return {
          type: 'none',
          title: '已撤回',
          desc: '这次审核单已撤回，可以改完重新提交',
        };
      case 1:
        return {
          type: 'fail',
          title: '审核未通过',
          desc: `驳回原因：${remote.reason || (this.failRow && this.failRow.failReason) || '微信未返回原因，请到微信公众平台查看'}`,
        };
      case 0:
        if (this.passRow) {
          return {
            type: 'success',
            title: '审核通过，可以发布',
            desc: `${version}已通过审核，点「发布版本」即可上线`,
          };
        }
        if (this.local && this.local.online) {
          return this.onlineStatus();
        }
        /** 过审了但本地一条记录都没有（比如在微信公众平台提的审），别谎报已发布 */
        return {
          type: 'success',
          title: '审核已通过',
          desc: '本地没有对应的版本记录，请到微信公众平台确认是否需要发布',
        };
      /** 微信以后加状态值时不至于让模板取到 undefined */
      default:
        return {
          type: 'none',
          title: '审核状态未知',
          desc: '请到微信公众平台查看',
        };
    }
  }
  onlineStatus() {
    let online = this.local && this.local.online;
    return {
      type: 'success',
      title: '已发布上线',
      desc: online && online.releaseTime ? `${this.$format(online.releaseTime)} 发布` : '',
    };
  }

  /**
   * @param toast 只有手动点「刷新审核结果」才提示错误：
   *              首屏和轮询报错不该让没授权小程序的店铺一进来就红一片
   */
  async loadAuditResult(toast = false) {
    this.auditLoading = true;
    let { error, data, message } = await this.$get("/manager/open-platform/audit-result");
    this.auditLoading = false;
    if (error) {
      this.clearPoll();
      if (toast) this.$error(message);
      return;
    }
    this.auditInfo = data;
    this.scheduleNextPoll();
  }

  scheduleNextPoll() {
    this.clearPoll();
    if (!this.needPoll) return;
    this.pollTimer = setTimeout(() => this.loadAuditResult(), 30000);
  }

  clearPoll() {
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
  }

  beforeUnmount() {
    this.clearPoll();
  }

  @Action("store/getStore")
  getStore

  async created() {
    if (this.authString) {
      let { error } = await this.$post(
        "/manager/open-platform/auth-callback",
        this.authPost
      );
      if (error) return;
      this.$success("授权成功");
      this.$replace("/shop/store/wechat-auth");
      this.getStore()
    }
    this.getRegisterStatus();
  }
  async getRegisterStatus() {
    let { error, data } = await this.$get(
      "/manager/open-platform/register-status"
    );
    if (error) return;
    this.registerStatus = data;
  }

  registerMiniCode() {
    if (this.store.appid) return this.$alert("已绑定小程序无法注册");
    this.$dialog({
      title: "申请小程序",
      props: { width: "630px", },
      content: () => {
        return <RegisterMini source={this.registerStatus} />
      }
    });
  }

  async withdrawAudit() {
    let { error, message } = await this.$get(
      "/manager/open-platform/withdraw-audit"
    );
    if (error) return this.$error(message);
    this.$success("撤回审核成功");
    this.loadAuditResult();
  }

  async release() {
    let { error, message } = await this.$get("/manager/open-platform/release");
    if (error) return this.$error(message);
    this.$success("恭喜您发布代码成功");
    this.loadAuditResult();
  }
  async submitAuidt() {
    let { error, message } = await this.$get("/manager/open-platform/submit-audit");
    if (error) return this.$error(message);
    this.$success("提交审核成功");
    this.loadAuditResult();
  }
  async viewCode() {
    this.$dialog({
      title: "查看体验码",
      props: { width: "630px", },
      content: () => {
        return <Tester />
      }
    });
  }
  async gotoAuth() {
    this.authLoading = true;
    let { error, data, message } = await this.$get("/manager/open-platform/get-auth-url");
    this.authLoading = false;
    if (error) return this.$error(message);
    location.href = data.url;
  }
  paySetting() {
    this.$dialog({
      title: "支付设置",
      props: { width: "530px", },
      content() {
        return <Pay />;
      }
    });
  }
}
